import {
  LegalAuthority,
  LegalObligation,
  ComplianceControl,
  ComplianceEvidence,
  ProcessingActivity,
  PrivacyRightsRequest,
  TrackingTechnology,
  InternationalTransfer,
  Subprocessor,
  FinancialRegulatoryActivity,
  ContextualDisclosure,
  AISystemGovernance,
  AccessibilityAuditRecord,
  RetentionPolicyItem,
  LegalHoldRecord,
  MarketComplianceProfile,
  LegalDocumentVersion,
  RegulatoryChangeRecord,
  ComplianceIncidentRecord,
  CCOExecutiveTelemetry
} from './complianceTypes';

export const CCO_EXECUTIVE_METRICS: CCOExecutiveTelemetry = {
  totalMonitoredMarkets: 48,
  legallyReadyPaidMarkets: 7, // US, CA, GB, FR, DE, CI, SN
  blockedOrFreeOnlyMarkets: 41,
  openPrivacyRequests: 3,
  overduePrivacyRequests: 0,
  registeredTrackersCount: 6,
  unregisteredTrackersAlertCount: 0,
  activeSubprocessorsCount: 8,
  activeLegalHoldsCount: 1,
  criticalComplianceIncidentsCount: 0,
  criticalAccessibilityBlockersCount: 0,
  upcomingDeadlinesNext30Days: 2,
  paidMonetizationMasterMode: 'OFF'
};

export const LEGAL_AUTHORITIES: LegalAuthority[] = [
  {
    id: 'auth-eu-gdpr',
    code: 'EU-REG-2016-679',
    name: 'General Data Protection Regulation (EU GDPR)',
    jurisdiction: 'European Union / EEA',
    authorityType: 'REGULATION',
    officialSource: 'https://eur-lex.europa.eu/eli/reg/2016/679/oj',
    governingBody: 'European Data Protection Board (EDPB) & National DPAs',
    status: 'ACTIVE',
    effectiveFrom: '2018-05-25',
    lastVerifiedAt: '2026-08-20',
    nextReviewAt: '2026-11-20',
    owner: 'Chief Privacy Officer',
    legalReviewer: 'Senior EU Data Protection Counsel'
  },
  {
    id: 'auth-uk-gdpr',
    code: 'UK-DPA-2018-DUAA',
    name: 'UK GDPR & Data (Use and Access) Framework',
    jurisdiction: 'United Kingdom',
    authorityType: 'LAW',
    officialSource: 'https://www.legislation.gov.uk/ukpga/2018/12/contents',
    governingBody: 'Information Commissioner’s Office (ICO)',
    status: 'ACTIVE',
    effectiveFrom: '2021-01-01',
    lastVerifiedAt: '2026-08-22',
    nextReviewAt: '2026-11-22',
    owner: 'Chief Privacy Officer',
    legalReviewer: 'UK Regulatory Counsel'
  },
  {
    id: 'auth-us-ca-cpra',
    code: 'CAL-CIV-1798-100',
    name: 'California Consumer Privacy Act as amended by CPRA',
    jurisdiction: 'United States (California)',
    authorityType: 'LAW',
    officialSource: 'https://cppa.ca.gov/regulations/',
    governingBody: 'California Privacy Protection Agency (CPPA)',
    status: 'ACTIVE',
    effectiveFrom: '2023-01-01',
    lastVerifiedAt: '2026-08-15',
    nextReviewAt: '2026-11-15',
    owner: 'US Compliance Lead',
    legalReviewer: 'US Privacy Counsel'
  },
  {
    id: 'auth-eu-ai-act',
    code: 'EU-REG-2024-1689',
    name: 'EU Artificial Intelligence Act (EU AI Act)',
    jurisdiction: 'European Union',
    authorityType: 'REGULATION',
    officialSource: 'https://eur-lex.europa.eu/eli/reg/2024/1689/oj',
    governingBody: 'European AI Office & Member State Authorities',
    status: 'ACTIVE',
    effectiveFrom: '2024-08-01',
    lastVerifiedAt: '2026-08-18',
    nextReviewAt: '2026-10-01',
    owner: 'AI Governance Lead',
    legalReviewer: 'Technology & AI Counsel'
  },
  {
    id: 'auth-eu-eaa',
    code: 'EU-DIR-2019-882',
    name: 'European Accessibility Act (EAA) & EN 301 549',
    jurisdiction: 'European Union',
    authorityType: 'DIRECTIVE',
    officialSource: 'https://eur-lex.europa.eu/eli/dir/2019/882/oj',
    governingBody: 'EU Member State Market Surveillance Authorities',
    status: 'ACTIVE',
    effectiveFrom: '2025-06-28',
    lastVerifiedAt: '2026-08-10',
    nextReviewAt: '2026-12-01',
    owner: 'Accessibility Lead',
    legalReviewer: 'Consumer & Accessibility Counsel'
  },
  {
    id: 'auth-pci-dss',
    code: 'PCI-DSS-V4-0-1',
    name: 'Payment Card Industry Data Security Standard (PCI DSS 4.0.1)',
    jurisdiction: 'GLOBAL',
    authorityType: 'INDUSTRY_STANDARD',
    officialSource: 'https://www.pcisecuritystandards.org/',
    governingBody: 'PCI Security Standards Council',
    status: 'ACTIVE',
    effectiveFrom: '2024-06-11',
    lastVerifiedAt: '2026-08-12',
    nextReviewAt: '2027-03-31',
    owner: 'Security & Payments Architect',
    legalReviewer: 'Payments Counsel'
  }
];

export const LEGAL_OBLIGATIONS: LegalObligation[] = [
  {
    id: 'obl-01-gdpr-transparency',
    authorityId: 'auth-eu-gdpr',
    jurisdiction: 'European Union',
    category: 'PRIVACY',
    title: 'Layered Transparent Privacy Notice & Purpose Specification (Art. 13/14)',
    summary: 'YamooPay must provide clear, accessible, layered privacy notices specifying exact data categories, purposes, lawful bases, and transfer mechanisms.',
    applicabilityConditions: ['Processing personal data of EEA users or operating web presence directed at EU'],
    effectiveDate: '2018-05-25',
    riskLevel: 'HIGH',
    owner: 'Chief Privacy Officer',
    controlMappings: ['ctrl-privacy-notice-layered', 'ctrl-ropa-sync'],
    evidenceRequirements: ['Published versioned Privacy Policy', 'Contextual form disclosures'],
    status: 'CONTROL_IMPLEMENTED_EVIDENCE_AVAILABLE',
    lastAssessedAt: '2026-08-20'
  },
  {
    id: 'obl-02-dsar-sla',
    authorityId: 'auth-eu-gdpr',
    jurisdiction: 'European Union',
    category: 'PRIVACY',
    title: 'Data Subject Rights Fulfillment within 30-Day Statutory Limit (Art. 15-22)',
    summary: 'Provide verified mechanisms for access, rectification, deletion, portability, restriction, and objection without undue delay.',
    applicabilityConditions: ['All registered users and public data subjects subject to GDPR'],
    effectiveDate: '2018-05-25',
    riskLevel: 'CRITICAL',
    owner: 'Privacy Operations Manager',
    controlMappings: ['ctrl-dsar-portal', 'ctrl-cross-system-deletion'],
    evidenceRequirements: ['Audit logs of completed requests', 'Retention exception records'],
    status: 'CONTROL_IMPLEMENTED_EVIDENCE_AVAILABLE',
    lastAssessedAt: '2026-08-20'
  },
  {
    id: 'obl-03-fin-disclaimer',
    authorityId: 'auth-eu-gdpr',
    jurisdiction: 'GLOBAL',
    category: 'FINANCIAL_SERVICES',
    title: 'FinTech Regulatory Boundary & Non-Advisory Clear Disclosures',
    summary: 'Calculators must clearly display informational and non-binding status. Never state "Loan approved", "Guaranteed return", or issue individual stock buy advice.',
    applicabilityConditions: ['All financial intelligence calculators (FX, Loans, Taxes, Salaries, FIRE)'],
    effectiveDate: '2026-01-01',
    riskLevel: 'CRITICAL',
    owner: 'General Counsel',
    controlMappings: ['ctrl-calc-contextual-disclaimers', 'ctrl-ai-advice-boundary'],
    evidenceRequirements: ['UI regression tests on all calculator result cards'],
    status: 'CONTROL_IMPLEMENTED_EVIDENCE_AVAILABLE',
    lastAssessedAt: '2026-08-22'
  },
  {
    id: 'obl-04-ai-transparency',
    authorityId: 'auth-eu-ai-act',
    jurisdiction: 'European Union',
    category: 'AI',
    title: 'AI Chatbot Transparency & Non-Human Identification (Art. 50)',
    summary: 'Users must be informed in an obvious, unambiguous manner that they are interacting with an AI system.',
    applicabilityConditions: ['YamooPay AI Assistant and RAG conversational features'],
    effectiveDate: '2024-08-01',
    riskLevel: 'HIGH',
    owner: 'AI Governance Lead',
    controlMappings: ['ctrl-ai-disclaimer-banner', 'ctrl-ai-prohibited-use-guard'],
    evidenceRequirements: ['Screenshot and automated UI assertions of persistent AI badge'],
    status: 'CONTROL_IMPLEMENTED_EVIDENCE_AVAILABLE',
    lastAssessedAt: '2026-08-18'
  },
  {
    id: 'obl-05-consumer-cancellation',
    authorityId: 'auth-eu-gdpr',
    jurisdiction: 'European Union / UK / US',
    category: 'CONSUMER_PROTECTION',
    title: 'Pre-Contract Price Transparency & Frictionless Cancellation (No Dark Patterns)',
    summary: 'Clearly display all recurring prices, tax components, renewal dates, and provide online 1-click cancellation matching subscription signup ease.',
    applicabilityConditions: ['Commercial checkouts and paid subscription plans (Prompt 21)'],
    effectiveDate: '2026-01-01',
    riskLevel: 'HIGH',
    owner: 'Consumer Protection Counsel',
    controlMappings: ['ctrl-checkout-price-breakdown', 'ctrl-portal-cancel-button'],
    evidenceRequirements: ['Checkout preview snapshots', 'Cancellation workflow telemetry'],
    status: 'CONTROL_IMPLEMENTED_EVIDENCE_AVAILABLE',
    lastAssessedAt: '2026-08-24'
  }
];

export const PROCESSING_ACTIVITIES_ROPA: ProcessingActivity[] = [
  {
    id: 'ropa-01-account-management',
    name: 'User Account & Passkey Authentication Management',
    purpose: 'Provision user profiles, secure authentication, multi-factor sessions, and customer account administration.',
    dataSubjects: ['Registered YamooPay users'],
    dataCategories: ['Email address', 'Hashed password / Passkey public credentials', 'Login timestamps', 'IP address'],
    lawfulBasis: 'CONTRACT',
    systemsInvolved: ['PostgreSQL Auth Store', 'Identity Engine', 'Session Redis'],
    recipients: ['Cloud Infrastructure hosting providers'],
    processors: ['Google Cloud Run / AWS', 'SendGrid (Transactional email)'],
    internationalTransfers: ['trans-us-gcp-scc'],
    retentionPeriod: 'Duration of active account + 30 days post-deletion grace period',
    securityControls: ['Argon2id password hashing', 'WebAuthn hardware encryption', 'TLS 1.3 in transit'],
    owner: 'Identity & Security Lead',
    lastReviewedAt: '2026-08-15'
  },
  {
    id: 'ropa-02-financial-calculations',
    name: 'Personal Financial Scenario Storage & Calculation Histories',
    purpose: 'Persist user-saved loan, salary tax, compound interest, and currency scenarios for multi-device synchronization.',
    dataSubjects: ['Authenticated power users'],
    dataCategories: ['Saved loan amounts', 'Interest rates', 'Salary tax brackets', 'Investment goals'],
    lawfulBasis: 'CONTRACT',
    systemsInvolved: ['PostgreSQL Calculator Store', 'My YamooPay Hub'],
    recipients: ['Strictly internal; zero commercial third-party sale'],
    processors: ['Google Cloud Databases'],
    internationalTransfers: ['trans-us-gcp-scc'],
    retentionPeriod: 'Until user deletes scenario or closes account',
    securityControls: ['AES-256 field-level encryption for sensitive debt records', 'RBAC access policies'],
    owner: 'Chief Product Officer',
    lastReviewedAt: '2026-08-15'
  },
  {
    id: 'ropa-03-billing-subscriptions',
    name: 'Subscription Billing, Tax Invoicing & Payment Processing',
    purpose: 'Process SaaS & API developer subscriptions, generate VAT/sales tax invoices, and satisfy statutory financial accounting laws.',
    dataSubjects: ['Paying customers & developer tier subscribers'],
    dataCategories: ['Billing name', 'Billing email', 'Last 4 card digits', 'Payment token', 'Tax VAT/GST ID', 'Invoice line items'],
    lawfulBasis: 'CONTRACT',
    systemsInvolved: ['Stripe Adapter', 'Moneroo Adapter', 'Chariow Adapter', 'Invoice Ledger DB'],
    recipients: ['Payment processors', 'Tax Authorities upon audited statutory filing'],
    processors: ['Stripe Inc.', 'Moneroo Payments', 'Chariow SAS'],
    internationalTransfers: ['trans-stripe-dpf'],
    retentionPeriod: '10 years following transaction date (Mandatory Statutory Accounting Law)',
    securityControls: ['Zero plaintext card storage (PCI DSS Level 1 Tokenization)', 'HMAC webhook signature validation'],
    owner: 'Chief Revenue & Financial Officer',
    lastReviewedAt: '2026-08-24'
  },
  {
    id: 'ropa-04-first-party-analytics',
    name: 'First-Party Product Telemetry & Error Monitoring',
    purpose: 'Understand platform performance, detect calculation calculation exceptions, and improve UI ergonomics without third-party ad sharing.',
    dataSubjects: ['All website visitors and app users'],
    dataCategories: ['Anonymized page paths', 'Calculator usage counts', 'Device viewport', 'Synthetic session ID'],
    lawfulBasis: 'LEGITIMATE_INTEREST',
    legitimateInterestAssessmentId: 'lia-analytics-2026-01',
    systemsInvolved: ['Internal Analytics Engine', 'Sentry Error Monitor'],
    recipients: ['Internal engineering and telemetry systems'],
    processors: ['Sentry / Functional Error Ingestion'],
    internationalTransfers: ['trans-sentry-scc'],
    retentionPeriod: '90 days rolling aggregation, then strictly purged',
    securityControls: ['IP address truncation / anonymization', 'Zero PII query parameter stripping'],
    owner: 'Analytics & DevOps Lead',
    lastReviewedAt: '2026-08-10'
  }
];

export const PRIVACY_RIGHTS_REQUESTS: PrivacyRightsRequest[] = [
  {
    id: 'dsar-2026-08-01',
    trackingCode: 'YP-DSAR-7719A',
    requesterEmail: 'david.lefevre@finance.fr',
    rightType: 'DELETE',
    jurisdiction: 'European Union (France - CNIL)',
    receivedAt: '2026-08-20T10:14:00Z',
    verificationMethod: 'AUTHENTICATED_SESSION',
    verificationStatus: 'VERIFIED',
    statutoryDeadline: '2026-09-19T23:59:59Z',
    status: 'FULFILLED',
    assignedTo: 'Data Protection Officer',
    resolutionSummary: 'User account, saved loan scenarios, and telemetry identities purged across all microservices. Immutable statutory invoice records retained with pseudonymized billing reference under Art. 17(3)(b).',
    retainedRecordsExplanation: 'Tax invoice YP-INV-2026-0842 retained for 10-year statutory tax compliance.'
  },
  {
    id: 'dsar-2026-08-02',
    trackingCode: 'YP-DSAR-8820B',
    requesterEmail: 'elena.rostova@techinvest.co.uk',
    rightType: 'PORTABILITY',
    jurisdiction: 'United Kingdom (ICO)',
    receivedAt: '2026-08-23T14:30:00Z',
    verificationMethod: 'EMAIL_OTP',
    verificationStatus: 'VERIFIED',
    statutoryDeadline: '2026-09-22T23:59:59Z',
    status: 'IN_PROGRESS',
    assignedTo: 'Privacy Operations Lead',
    resolutionSummary: 'Automated JSON export generated and encrypted with temporary 24-hour single-use download link.'
  },
  {
    id: 'dsar-2026-08-03',
    trackingCode: 'YP-DSAR-9931C',
    requesterEmail: 'jason.vance@californiagrowth.io',
    rightType: 'OPT_OUT_SALE_SHARE',
    jurisdiction: 'United States (California CCPA/CPRA)',
    receivedAt: '2026-08-24T08:00:00Z',
    verificationMethod: 'AUTHENTICATED_SESSION',
    verificationStatus: 'VERIFIED',
    statutoryDeadline: '2026-09-08T23:59:59Z',
    status: 'FULFILLED',
    assignedTo: 'US Privacy Compliance Analyst',
    resolutionSummary: 'Global Privacy Control (GPC) signal honored automatically. Advertising flags set to strict non-personalized mode with zero third-party cross-context sharing.'
  }
];

export const TRACKING_TECHNOLOGIES_REGISTRY: TrackingTechnology[] = [
  {
    id: 'trk-01-sess',
    name: 'yp_session_token',
    provider: 'YamooPay Native First-Party',
    technologyType: 'COOKIE',
    category: 'ESSENTIAL',
    purpose: 'Maintains authenticated secure login session and protects against CSRF cross-site request forgery.',
    lifespan: 'Session / 30 days for Remember Me',
    domain: '.yamoopay.com',
    isFirstParty: true,
    requiresConsentIn: [], // Exempt from consent as strictly essential
    status: 'REGISTERED_ALLOWED',
    lastVerifiedAt: '2026-08-20'
  },
  {
    id: 'trk-02-consent',
    name: 'yp_consent_preferences',
    provider: 'YamooPay Native CMP',
    technologyType: 'LOCAL_STORAGE',
    category: 'ESSENTIAL',
    purpose: 'Stores user consent choices across analytics, advertising, and localization preferences.',
    lifespan: '12 months',
    domain: '.yamoopay.com',
    isFirstParty: true,
    requiresConsentIn: [],
    status: 'REGISTERED_ALLOWED',
    lastVerifiedAt: '2026-08-20'
  },
  {
    id: 'trk-03-fx-cache',
    name: 'yp_offline_rates_db',
    provider: 'YamooPay PWA Engine',
    technologyType: 'INDEXEDDB',
    category: 'FUNCTIONAL',
    purpose: 'Caches offline currency pairs and salary tax formulas for fast mobile offline calculations.',
    lifespan: 'Persistent until manual clear',
    domain: '.yamoopay.com',
    isFirstParty: true,
    requiresConsentIn: [],
    status: 'REGISTERED_ALLOWED',
    lastVerifiedAt: '2026-08-20'
  },
  {
    id: 'trk-04-analytics',
    name: 'yp_fp_analytics_id',
    provider: 'YamooPay Telemetry System',
    technologyType: 'LOCAL_STORAGE',
    category: 'ANALYTICS',
    purpose: 'Measures high-level calculator error rates and feature adoption with pseudonymized synthetic IDs.',
    lifespan: '90 days',
    domain: '.yamoopay.com',
    isFirstParty: true,
    requiresConsentIn: ['EU', 'UK', 'BR', 'CA'],
    status: 'REGISTERED_ALLOWED',
    lastVerifiedAt: '2026-08-20'
  },
  {
    id: 'trk-05-adsense',
    name: '__gads / IDE',
    provider: 'Google AdSense / TCF Vendor',
    technologyType: 'COOKIE',
    category: 'ADVERTISING',
    purpose: 'Serves contextual or non-personalized banner advertisements in designated commercial slots.',
    lifespan: '13 months',
    domain: '.doubleclick.net',
    isFirstParty: false,
    requiresConsentIn: ['EU', 'UK', 'CA', 'BR', 'AU'],
    status: 'REGISTERED_ALLOWED',
    lastVerifiedAt: '2026-08-20'
  },
  {
    id: 'trk-06-stripe',
    name: '__stripe_mid / __stripe_sid',
    provider: 'Stripe Payments',
    technologyType: 'COOKIE',
    category: 'ESSENTIAL',
    purpose: 'Fraud detection and tokenized checkout authentication on commercial billing surfaces.',
    lifespan: '1 year',
    domain: '.stripe.com',
    isFirstParty: false,
    requiresConsentIn: [], // Legally recognized fraud prevention exception
    status: 'REGISTERED_ALLOWED',
    lastVerifiedAt: '2026-08-20'
  }
];

export const SUBPROCESSORS_REGISTRY: Subprocessor[] = [
  {
    id: 'sub-gcp',
    name: 'Google Cloud Platform (GCP)',
    serviceProvided: 'Cloud hosting, container execution (Cloud Run), secure PostgreSQL databases, and object storage',
    dataCategories: ['User profile data', 'Encrypted scenarios', 'Application logs'],
    processingLocations: ['United States (us-east1)', 'European Union (europe-west1)'],
    dpaSigned: true,
    transferMechanism: 'EU Standard Contractual Clauses (SCCs) & Data Privacy Framework (DPF)',
    securityTier: 'TIER_1_ENTERPRISE',
    status: 'ACTIVE_APPROVED',
    contractRenewalDate: '2027-01-15'
  },
  {
    id: 'sub-stripe',
    name: 'Stripe Inc.',
    serviceProvided: 'Payment gateway, card tokenization, and recurring SaaS billing adapter',
    dataCategories: ['Billing contact email', 'Payment method tokens', 'Transaction amounts'],
    processingLocations: ['United States', 'Ireland (Stripe Payments Europe)'],
    dpaSigned: true,
    transferMechanism: 'EU-US Data Privacy Framework (Certified) & SCCs',
    securityTier: 'TIER_1_ENTERPRISE',
    status: 'ACTIVE_APPROVED',
    contractRenewalDate: '2027-05-01'
  },
  {
    id: 'sub-moneroo',
    name: 'Moneroo Multi-Channel Payments',
    serviceProvided: 'Mobile Money and Pan-African multi-currency payment checkout integration',
    dataCategories: ['Customer email', 'Mobile money transaction IDs', 'Local currency amounts'],
    processingLocations: ['France', 'Côte d’Ivoire', 'Senegal'],
    dpaSigned: true,
    transferMechanism: 'Direct European DPA with Standard Safeguards',
    securityTier: 'TIER_2_VERIFIED',
    status: 'ACTIVE_APPROVED',
    contractRenewalDate: '2026-12-31'
  },
  {
    id: 'sub-chariow',
    name: 'Chariow SAS',
    serviceProvided: 'European and West African payment gateway & pulse checkout',
    dataCategories: ['Order references', 'Billing metadata', 'Euro/CFA Franc settlement'],
    processingLocations: ['France', 'Benin'],
    dpaSigned: true,
    transferMechanism: 'Direct European GDPR compliant DPA',
    securityTier: 'TIER_2_VERIFIED',
    status: 'ACTIVE_APPROVED',
    contractRenewalDate: '2027-03-15'
  },
  {
    id: 'sub-google-genai',
    name: 'Google Gemini AI APIs (@google/genai)',
    serviceProvided: 'Financial knowledge assistant, query interpretation, and contextual RAG tool calling',
    dataCategories: ['De-identified user prompt text (Zero PII policy)'],
    processingLocations: ['United States'],
    dpaSigned: true,
    transferMechanism: 'Google Cloud Enterprise AI Terms (Zero customer data training pledge)',
    securityTier: 'TIER_1_ENTERPRISE',
    status: 'ACTIVE_APPROVED',
    contractRenewalDate: '2027-01-15'
  }
];

export const FINANCIAL_REGULATORY_ACTIVITIES: FinancialRegulatoryActivity[] = [
  {
    id: 'fin-act-01-calc',
    featureName: 'Universal Financial Calculators (Loan, Mortgage, Salary, FIRE, FX)',
    activityType: 'FINANCIAL_CALCULATION',
    markets: ['GLOBAL'],
    holdsFunds: false,
    initiatesTransfers: false,
    providesCreditDecision: false,
    providesInvestmentAdvice: false,
    regulatoryRisk: 'LOW',
    regulatoryStatus: 'DISCLOSURE_REQUIRED',
    approvedByLegal: true,
    approver: 'FinTech Regulatory Counsel',
    killSwitchCode: 'ks_calc_engine',
    requiredDisclosureCodes: ['DISC_CALC_ESTIMATE', 'DISC_NOT_TAX_ADVICE', 'DISC_NOT_CREDIT_APPROVAL']
  },
  {
    id: 'fin-act-02-transfer-compare',
    featureName: 'Money Transfer Provider Comparison Engine (Prompt 05)',
    activityType: 'TRANSFER_COMPARISON',
    markets: ['GLOBAL'],
    holdsFunds: false,
    initiatesTransfers: false, // Strictly comparison and referral only
    providesCreditDecision: false,
    providesInvestmentAdvice: false,
    regulatoryRisk: 'MEDIUM',
    regulatoryStatus: 'DISCLOSURE_REQUIRED',
    approvedByLegal: true,
    approver: 'Chief Legal & Compliance Officer',
    killSwitchCode: 'ks_transfer_compare',
    requiredDisclosureCodes: ['DISC_TRANSFER_ROLE', 'DISC_AFFILIATE_RELATIONSHIP']
  },
  {
    id: 'fin-act-03-ai-assistant',
    featureName: 'AI Financial Information & RAG Assistant (Prompt 15)',
    activityType: 'FINANCIAL_INFORMATION',
    markets: ['GLOBAL'],
    holdsFunds: false,
    initiatesTransfers: false,
    providesCreditDecision: false,
    providesInvestmentAdvice: false, // Strict negative prompt preventing stock recommendations
    regulatoryRisk: 'HIGH',
    regulatoryStatus: 'DISCLOSURE_REQUIRED',
    approvedByLegal: true,
    approver: 'Technology & AI Counsel',
    killSwitchCode: 'ks_ai_assistant_global',
    requiredDisclosureCodes: ['DISC_AI_GENERATED', 'DISC_NOT_INVESTMENT_ADVICE']
  },
  {
    id: 'fin-act-04-money-custody',
    featureName: 'Direct Money Transmission / Holding User Funds (Hypothetical Future)',
    activityType: 'MONEY_TRANSMISSION',
    markets: ['NONE'],
    holdsFunds: true,
    initiatesTransfers: true,
    providesCreditDecision: false,
    providesInvestmentAdvice: false,
    regulatoryRisk: 'CRITICAL',
    regulatoryStatus: 'PROHIBITED_CURRENT_MODEL',
    approvedByLegal: false,
    approver: 'BLOCKED BY POLICY (Requires Banking / EMI License)',
    killSwitchCode: 'ks_fund_custody_hard_lock',
    requiredDisclosureCodes: []
  }
];

export const CONTEXTUAL_DISCLOSURES: ContextualDisclosure[] = [
  {
    id: 'disc-01',
    code: 'DISC_CALC_ESTIMATE',
    title: 'Calculation Estimate & Non-Binding Status',
    textPlainEn: 'YamooPay calculations are mathematical estimates intended strictly for educational and general informational purposes. Actual financial terms, taxes, and interest charges are governed exclusively by your official contractual agreement with your licensed financial provider or tax authority.',
    textPlainFr: 'Les calculs YamooPay sont des estimations mathématiques à vocation purement éducative et informative. Les conditions réelles, impôts et taux sont régis par vos contrats avec vos institutions financières ou administrations fiscales.',
    type: 'CALCULATION_ESTIMATE',
    placementSurface: 'Below all Loan, Mortgage, Salary, and Retirement calculator results'
  },
  {
    id: 'disc-02',
    code: 'DISC_TRANSFER_ROLE',
    title: 'Money Transfer Comparison Role & Non-Sender Notice',
    textPlainEn: 'YamooPay is an independent financial rate intelligence and comparison platform. YamooPay does not execute money transfers, accept customer deposits, or hold funds. All remittance transactions are processed directly on licensed third-party platforms.',
    textPlainFr: 'YamooPay est une plateforme indépendante de comparaison de tarifs. YamooPay n’effectue aucun transfert de fonds, ne reçoit aucun dépôt et ne conserve aucun fond. Les transactions sont traitées par des prestataires agréés tiers.',
    type: 'TRANSFER_PROVIDER_ROLE',
    placementSurface: 'Money transfer rate table headers and outbound provider links'
  },
  {
    id: 'disc-03',
    code: 'DISC_NOT_INVESTMENT_ADVICE',
    title: 'Not Regulated Investment Advice',
    textPlainEn: 'YamooPay and its AI assistants provide financial intelligence and simulation tools, not personalized investment advice, wealth management services, or security recommendations. Consult a qualified fiduciary financial advisor before making investment decisions.',
    textPlainFr: 'YamooPay et son assistant IA fournissent des outils de simulation et d’information, et non des conseils personnalisés en investissement financier. Consultez un conseiller financier agréé pour vos décisions de placement.',
    type: 'NOT_INVESTMENT_ADVICE',
    placementSurface: 'FIRE calculators, investment growth charts, and AI Assistant chat headers'
  },
  {
    id: 'disc-04',
    code: 'DISC_AI_GENERATED',
    title: 'AI-Generated Content & Transparency Notice',
    textPlainEn: 'Responses in this dialogue are synthesized by an artificial intelligence system (Gemini). While designed with high-assurance financial logic, answers must not be treated as certified legal, tax, or financial advisory determinations.',
    textPlainFr: 'Les réponses de cette discussion sont générées par un système d’intelligence artificielle. Bien que calibrées avec rigueur, elles ne constituent pas un avis juridique, fiscal ou financier officiel.',
    type: 'AI_GENERATED',
    placementSurface: 'AI Chat drawer banner and message footers'
  }
];

export const AI_SYSTEM_GOVERNANCE: AISystemGovernance[] = [
  {
    id: 'ai-sys-01-assistant',
    systemName: 'YamooPay Financial Information RAG Assistant',
    purpose: 'Assist users in understanding currency trends, financial definitions, salary deductions, and calculator usage.',
    modelProvider: 'Google Gemini 2.5/3.0 via @google/genai',
    yamooPayRole: 'DEPLOYER',
    aiActRiskClass: 'TRANSPARENCY_RISK',
    userFacingChatbot: true,
    transparencyNoticeRequired: true,
    transparencyNoticeActive: true,
    decisionImpact: 'INFORMATIONAL_ASSISTANCE_ONLY',
    humanOversightMechanism: 'Automated policy boundary filters with manual sample evaluation and user feedback reporting',
    prohibitedPracticeCheckPassed: true,
    status: 'AUTHORIZED',
    lastEvaluatedAt: '2026-08-18'
  },
  {
    id: 'ai-sys-02-credit-scoring',
    systemName: 'Automated Underwriting / Credit Decision Engine (Prohibited Module)',
    purpose: 'Attempting to calculate individual creditworthiness scores for loan granting',
    modelProvider: 'N/A',
    yamooPayRole: 'PROVIDER',
    aiActRiskClass: 'HIGH_RISK',
    userFacingChatbot: false,
    transparencyNoticeRequired: true,
    transparencyNoticeActive: false,
    decisionImpact: 'PROHIBITED_FINANCIAL_DECISION',
    humanOversightMechanism: 'BLOCKED BY POLICY — YamooPay explicitly forbids credit decisioning algorithms',
    prohibitedPracticeCheckPassed: false,
    status: 'BLOCKED_BY_POLICY',
    lastEvaluatedAt: '2026-08-18'
  }
];

export const ACCESSIBILITY_AUDIT_RECORDS: AccessibilityAuditRecord[] = [
  {
    id: 'a11y-01-contrast',
    componentOrFlow: 'Primary Navigation & Calculator Number Inputs',
    wcagCriterion: 'WCAG 2.2 1.4.3 Contrast (Minimum)',
    level: 'AA',
    europeanAccessibilityActApplicable: true,
    testingMethod: 'HIGH_CONTRAST_REFLOW',
    status: 'PASSED',
    reportedAt: '2026-08-20',
    remediationOwner: 'UI Design System Lead',
    remediationDeadline: '2026-08-20'
  },
  {
    id: 'a11y-02-keyboard',
    componentOrFlow: 'Currency Picker Dropdown & Quick Switcher',
    wcagCriterion: 'WCAG 2.2 2.1.1 Keyboard Accessible & 2.4.7 Focus Visible',
    level: 'AA',
    europeanAccessibilityActApplicable: true,
    testingMethod: 'MANUAL_KEYBOARD',
    status: 'PASSED',
    reportedAt: '2026-08-21',
    remediationOwner: 'Frontend Accessibility Lead',
    remediationDeadline: '2026-08-21'
  },
  {
    id: 'a11y-03-screenreader',
    componentOrFlow: 'Payment Checkout & Plan Interval Toggle Buttons',
    wcagCriterion: 'WCAG 2.2 4.1.2 Name, Role, Value (ARIA-live status)',
    level: 'AA',
    europeanAccessibilityActApplicable: true,
    testingMethod: 'SCREEN_READER_NVDA_VOICEOVER',
    status: 'PASSED',
    reportedAt: '2026-08-24',
    remediationOwner: 'Payments Frontend Lead',
    remediationDeadline: '2026-08-24'
  }
];

export const RETENTION_POLICIES: RetentionPolicyItem[] = [
  {
    id: 'ret-01-invoices',
    recordCategory: 'Commercial Invoices & Tax Transaction Records',
    jurisdiction: 'GLOBAL / EU (Code de commerce) / US (IRS Sec. 6001)',
    purpose: 'Compliance with statutory accounting and tax audit record retention mandates.',
    retentionPeriod: '10 Years',
    triggerEvent: 'INVOICE_ISSUED_AT',
    legalBasis: 'Legal Obligation (Tax & Commercial Code)',
    deletionMethod: 'HARD_DELETE_DATABASE',
    legalHoldActiveCount: 0
  },
  {
    id: 'ret-02-auth-sessions',
    recordCategory: 'Authentication Tokens & Session Identifiers',
    jurisdiction: 'GLOBAL',
    purpose: 'Active user authentication and CSRF session security.',
    retentionPeriod: '30 Days post-inactivity',
    triggerEvent: 'CREATED_AT',
    legalBasis: 'Contractual Necessity & Legitimate Interest',
    deletionMethod: 'HARD_DELETE_DATABASE',
    legalHoldActiveCount: 0
  },
  {
    id: 'ret-03-dsar-logs',
    recordCategory: 'Data Subject Privacy Rights Request Audit Trails',
    jurisdiction: 'EU / UK / US-CA',
    purpose: 'Evidence of regulatory compliance under GDPR Art. 5(2) accountability and CCPA records.',
    retentionPeriod: '24 Months post-fulfillment',
    triggerEvent: 'INCIDENT_CLOSED_AT',
    legalBasis: 'Legal Obligation',
    deletionMethod: 'ANONYMIZATION',
    legalHoldActiveCount: 0
  },
  {
    id: 'ret-04-disputed-case',
    recordCategory: 'Disputed Merchant Ledger Transaction Records',
    jurisdiction: 'US / EU',
    purpose: 'Preservation for ongoing regulatory inquiry and chargeback adjudication.',
    retentionPeriod: 'Indefinite while Legal Hold is active',
    triggerEvent: 'INCIDENT_CLOSED_AT',
    legalBasis: 'Legal Hold / Defense of Legal Claims',
    deletionMethod: 'HARD_DELETE_DATABASE',
    legalHoldActiveCount: 1
  }
];

export const LEGAL_HOLDS: LegalHoldRecord[] = [
  {
    id: 'hold-2026-01',
    caseReference: 'LH-CASE-FIN-2026-08',
    title: 'Payment Chargeback Arbitration Ledger Preservation Hold',
    scopeDescription: 'Preserves raw provider settlement payloads and invoice timestamps for transaction txn_881920_str pending card network arbitration.',
    targetEntities: ['PaymentTransaction', 'BillingInvoice', 'AuditLog'],
    issuedAt: '2026-08-22T14:00:00Z',
    issuedBy: 'General Counsel',
    status: 'ACTIVE_HOLD_FREEZES_DELETION',
    reviewDate: '2026-11-22'
  }
];

export const MARKET_COMPLIANCE_PROFILES: MarketComplianceProfile[] = [
  {
    countryCode: 'US',
    countryName: 'United States',
    flagEmoji: '🇺🇸',
    region: 'NORTH_AMERICA',
    operatingEntity: 'YamooPay Technologies Inc. (Delaware)',
    privacyPack: 'US_CPRA_MULTI_STATE',
    freeCalculatorsStatus: 'AVAILABLE',
    paidMonetizationGate: 'LEGAL_READY_LIVE',
    supportedPaymentProviders: ['STRIPE'],
    commercialTaxReadiness: 'READY_REGISTERED',
    consumerWithdrawalCoolingOffDays: 0, // State-by-state auto-renewal compliance applied
    aiAssistantAllowed: true,
    lastLegalReviewAt: '2026-08-22',
    legalReviewer: 'US Regulatory & Payments Counsel'
  },
  {
    countryCode: 'FR',
    countryName: 'France',
    flagEmoji: '🇫🇷',
    region: 'EUROPE',
    operatingEntity: 'YamooPay SAS (France / EU)',
    privacyPack: 'EU_GDPR',
    freeCalculatorsStatus: 'AVAILABLE',
    paidMonetizationGate: 'LEGAL_READY_LIVE',
    supportedPaymentProviders: ['STRIPE', 'CHARIOW'],
    commercialTaxReadiness: 'READY_REGISTERED',
    consumerWithdrawalCoolingOffDays: 14, // EU 14-day statutory distance selling withdrawal
    aiAssistantAllowed: true,
    lastLegalReviewAt: '2026-08-20',
    legalReviewer: 'EU Data Protection & Consumer Counsel'
  },
  {
    countryCode: 'DE',
    countryName: 'Germany',
    flagEmoji: '🇩🇪',
    region: 'EUROPE',
    operatingEntity: 'YamooPay SAS (France / EU)',
    privacyPack: 'EU_GDPR',
    freeCalculatorsStatus: 'AVAILABLE',
    paidMonetizationGate: 'LEGAL_READY_LIVE',
    supportedPaymentProviders: ['STRIPE', 'CHARIOW'],
    commercialTaxReadiness: 'READY_REGISTERED',
    consumerWithdrawalCoolingOffDays: 14,
    aiAssistantAllowed: true,
    lastLegalReviewAt: '2026-08-20',
    legalReviewer: 'EU Legal Team'
  },
  {
    countryCode: 'GB',
    countryName: 'United Kingdom',
    flagEmoji: '🇬🇧',
    region: 'UK',
    operatingEntity: 'YamooPay Technologies Ltd (UK)',
    privacyPack: 'UK_GDPR',
    freeCalculatorsStatus: 'AVAILABLE',
    paidMonetizationGate: 'LEGAL_READY_LIVE',
    supportedPaymentProviders: ['STRIPE'],
    commercialTaxReadiness: 'READY_REGISTERED',
    consumerWithdrawalCoolingOffDays: 14,
    aiAssistantAllowed: true,
    lastLegalReviewAt: '2026-08-22',
    legalReviewer: 'UK Regulatory Counsel'
  },
  {
    countryCode: 'CA',
    countryName: 'Canada',
    flagEmoji: '🇨🇦',
    region: 'NORTH_AMERICA',
    operatingEntity: 'YamooPay Technologies Inc.',
    privacyPack: 'CANADA_PIPEDA',
    freeCalculatorsStatus: 'AVAILABLE',
    paidMonetizationGate: 'LEGAL_READY_LIVE',
    supportedPaymentProviders: ['STRIPE'],
    commercialTaxReadiness: 'READY_REGISTERED',
    consumerWithdrawalCoolingOffDays: 10,
    aiAssistantAllowed: true,
    lastLegalReviewAt: '2026-08-21',
    legalReviewer: 'North American Counsel'
  },
  {
    countryCode: 'CI',
    countryName: 'Côte d’Ivoire (UEMOA / WAEMU)',
    flagEmoji: '🇨🇮',
    region: 'AFRICA',
    operatingEntity: 'YamooPay West Africa SAS',
    privacyPack: 'WEST_AFRICA_UEMOA',
    freeCalculatorsStatus: 'AVAILABLE',
    paidMonetizationGate: 'LEGAL_READY_LIVE',
    supportedPaymentProviders: ['MONEROO', 'CHARIOW'],
    commercialTaxReadiness: 'READY_REGISTERED',
    consumerWithdrawalCoolingOffDays: 7,
    aiAssistantAllowed: true,
    lastLegalReviewAt: '2026-08-23',
    legalReviewer: 'African Regional Regulatory Counsel'
  },
  {
    countryCode: 'SN',
    countryName: 'Senegal (UEMOA / WAEMU)',
    flagEmoji: '🇸🇳',
    region: 'AFRICA',
    operatingEntity: 'YamooPay West Africa SAS',
    privacyPack: 'WEST_AFRICA_UEMOA',
    freeCalculatorsStatus: 'AVAILABLE',
    paidMonetizationGate: 'LEGAL_READY_LIVE',
    supportedPaymentProviders: ['MONEROO', 'CHARIOW'],
    commercialTaxReadiness: 'READY_REGISTERED',
    consumerWithdrawalCoolingOffDays: 7,
    aiAssistantAllowed: true,
    lastLegalReviewAt: '2026-08-23',
    legalReviewer: 'African Regional Regulatory Counsel'
  },
  {
    countryCode: 'BR',
    countryName: 'Brazil',
    flagEmoji: '🇧🇷',
    region: 'LATAM',
    operatingEntity: 'YamooPay Technologies Inc.',
    privacyPack: 'REST_OF_WORLD',
    freeCalculatorsStatus: 'FREE_ONLY',
    paidMonetizationGate: 'LEGAL_LIMITED_FREE_ONLY',
    supportedPaymentProviders: [],
    commercialTaxReadiness: 'NOT_READY',
    consumerWithdrawalCoolingOffDays: 7,
    aiAssistantAllowed: true,
    lastLegalReviewAt: '2026-08-15',
    legalReviewer: 'Latin America Counsel',
    hardBlockReason: 'PIX direct local acquiring and IOF tax withholding registrations currently in preparation.'
  },
  {
    countryCode: 'KP',
    countryName: 'North Korea (DPRK)',
    flagEmoji: '🇰🇵',
    region: 'APAC',
    operatingEntity: 'N/A',
    privacyPack: 'REST_OF_WORLD',
    freeCalculatorsStatus: 'BLOCKED',
    paidMonetizationGate: 'LEGAL_BLOCKED',
    supportedPaymentProviders: [],
    commercialTaxReadiness: 'NOT_READY',
    consumerWithdrawalCoolingOffDays: 0,
    aiAssistantAllowed: false,
    lastLegalReviewAt: '2026-08-01',
    legalReviewer: 'OFAC & International Sanctions Officer',
    hardBlockReason: 'Mandatory International Trade Sanctions (OFAC / UN / EU Export Controls).'
  }
];

export const LEGAL_DOCUMENTS: LegalDocumentVersion[] = [
  {
    id: 'doc-tos-v3',
    slug: 'terms-of-service',
    title: 'YamooPay Global Terms of Service',
    version: '3.1.0',
    publishedAt: '2026-08-25T00:00:00Z',
    effectiveFrom: '2026-08-25T00:00:00Z',
    legalReviewer: 'General Counsel & Chief Compliance Officer',
    summaryOfChanges: 'Incorporated Prompt 21 Payment & Subscription terms, Developer API terms, and AI Assistant user responsibilities.',
    status: 'PUBLISHED',
    contentMarkdown: `
# YamooPay Terms of Service (Version 3.1.0)
**Effective Date: August 25, 2026**

## 1. Acceptance of Terms & Eligibility
Welcome to YamooPay. By accessing our websites, mobile applications, APIs, or interactive financial calculation utilities, you agree to be bound by these Terms of Service. If you do not agree to these terms, do not access or use our services.

## 2. Description of Services & Non-Advisory FinTech Boundaries
YamooPay is an independent financial intelligence, rate comparison, mathematical calculation, and developer software platform.
- **Informational Estimates Only**: All calculator outputs (including loan amortizations, paycheck deductions, tax simulations, compound interest tables, and currency conversions) are mathematical simulations provided for educational and general planning purposes.
- **Zero Fiduciary or Regulated Financial Advice**: YamooPay does not act as a registered broker-dealer, investment advisor, chartered tax accountant, lender, or bank.
- **Money Transfer Comparison Role**: YamooPay compares money transfer providers and redirects users to third-party licensed platforms. YamooPay does not accept customer deposits, transmit money, or take custody of funds.

## 3. Commercial Subscriptions & Paid Monetization (When Enabled)
- When Paid Monetization is activated via our Master Control, paid subscriptions (e.g. Developer API, White-Label Widget licenses) are billed on a recurring monthly or annual basis.
- Prices are transparently disclosed before checkout. Subscriptions auto-renew unless cancelled prior to the end of the current billing cycle.
- **Cancellation**: You may cancel recurring subscriptions at any time with zero penalty through your Customer Billing Portal.

## 4. Acceptable Use & Developer API License
You agree not to:
- Probe, reverse-engineer, or circumvent rate limits of YamooPay endpoints without an authorized API plan.
- Use YamooPay algorithms to build discriminatory credit-scoring systems or violate applicable financial regulations.
- Distribute spam or make unauthorized automated bulk requests.

## 5. Intellectual Property & Governing Law
All calculation algorithms, UI tokens, trademarks, and documentation are the exclusive property of YamooPay. These terms are governed by the laws of the applicable operating entity jurisdiction.
`
  },
  {
    id: 'doc-privacy-v3',
    slug: 'privacy-policy',
    title: 'YamooPay Global Privacy Policy & Data Protection Notice',
    version: '3.1.0',
    publishedAt: '2026-08-25T00:00:00Z',
    effectiveFrom: '2026-08-25T00:00:00Z',
    legalReviewer: 'Chief Privacy Officer & EU DPO',
    summaryOfChanges: 'Comprehensive RoPA alignment, California CPRA opt-out notices, and subprocessor updates.',
    status: 'PUBLISHED',
    contentMarkdown: `
# YamooPay Global Privacy Policy (Version 3.1.0)
**Effective Date: August 25, 2026**

## 1. Our Commitment to Privacy by Design
YamooPay is built on the fundamental principle of data minimization. We collect only what is strictly necessary to deliver our financial intelligence tools. We never sell your personal financial inputs, salaries, debts, or scenario data to third-party data brokers.

## 2. Personal Data We Collect
- **Account & Identity Data**: Email address, pseudonymized display name, and passkey authentication credentials.
- **User-Authored Scenario Data**: Loan amounts, income estimates, and savings goals saved explicitly to your My YamooPay profile.
- **Commercial Billing Data**: Tokenized transaction references, billing email, and VAT/tax identifiers managed via PCI-DSS certified processors (Stripe, Moneroo, Chariow). We never store raw credit card numbers.
- **Technical & Telemetry Data**: Pseudonymized device type, regional language preferences, and essential security logs.

## 3. Lawful Bases for Processing (GDPR / UK GDPR)
- **Contractual Performance**: Delivering user accounts, saving calculation histories, and servicing paid subscriptions.
- **Legal Obligation**: Maintaining statutory tax invoice ledgers and fulfilling data subject rights requests.
- **Legitimate Interests**: Protecting infrastructure from DDoS attacks, optimizing calculation latency, and maintaining security audit logs.
- **Consent**: Non-essential analytical telemetry and localized personalized communications.

## 4. Your Privacy Rights (GDPR, CCPA/CPRA, LGPD, PIPEDA)
Depending on your jurisdiction, you have the right to:
- Request access to and a machine-readable copy of your personal data.
- Request the deletion (erasure) or correction of your personal data.
- Opt out of cross-context behavioral advertising and personal data sharing (including automated honoring of Global Privacy Control / GPC signals).
- Lodge a complaint with your local supervisory authority (e.g. CNIL, ICO, CPPA).

To submit a request, use our interactive Data Subject Rights Portal or email \`dpo@yamoopay.com\`.
`
  },
  {
    id: 'doc-cookies-v3',
    slug: 'cookie-policy',
    title: 'YamooPay Cookie & Tracking Technology Governance Policy',
    version: '3.0.0',
    publishedAt: '2026-08-25T00:00:00Z',
    effectiveFrom: '2026-08-25T00:00:00Z',
    legalReviewer: 'Chief Privacy Officer',
    summaryOfChanges: 'Granular tracking technology registry categorization, pre-consent blocking rules, and GPC signal compliance.',
    status: 'PUBLISHED',
    contentMarkdown: `
# YamooPay Cookie & Tracking Governance Policy (Version 3.0.0)
**Effective Date: August 25, 2026**

## 1. What Technologies We Use
YamooPay utilizes cookies, LocalStorage, and IndexedDB to deliver fast, secure, and offline-capable financial calculations.

## 2. Categorization of Technologies
- **Essential Technologies (Always Active)**: Necessary for core security, passkey logins, CSRF protection, and storing your consent choices. These cannot be switched off in our systems.
- **Functional & Offline Storage**: IndexedDB offline rate caching for uninterrupted calculator usage during network dropouts.
- **Analytics & Performance (Consent Required in EU/UK)**: Pseudonymized first-party telemetry to detect broken calculations and measure latency.
- **Advertising (Consent Required in EU/UK)**: Contextual ad delivery managed strictly in accordance with Google-certified CMP standards.

## 3. Managing Your Preferences
You can update your consent settings at any time using the "Cookie Settings" link in the footer or by transmitting an automated Global Privacy Control (GPC) signal.
`
  },
  {
    id: 'doc-refund-v3',
    slug: 'refund-cancellation-policy',
    title: 'YamooPay Consumer Protection, Refund & Cancellation Policy',
    version: '3.0.0',
    publishedAt: '2026-08-25T00:00:00Z',
    effectiveFrom: '2026-08-25T00:00:00Z',
    legalReviewer: 'Consumer Protection Counsel',
    summaryOfChanges: 'Jurisdictional statutory cooling-off rights integration (EU/UK 14 days) and zero-dark-pattern cancellation rules.',
    status: 'PUBLISHED',
    contentMarkdown: `
# YamooPay Refund & Cancellation Policy (Version 3.0.0)
**Effective Date: August 25, 2026**

## 1. Fair Commercial Practices & Zero Dark Patterns
YamooPay is committed to absolute price transparency, zero hidden charges, and effortless cancellation matching the simplicity of subscription signup.

## 2. How to Cancel
You may cancel any active recurring subscription at any time:
1. Log in and navigate to the **Customer Billing Portal**.
2. Click **Cancel Subscription**.
3. Your plan will remain active until the end of your current prepaid billing period, and no further charges will be made.

## 3. Statutory Consumer Withdrawal Rights (Cooling-Off Period)
- **European Union & United Kingdom**: Consumers have the legal right to cancel their digital subscription within 14 days of purchase without giving any reason. If you have not utilized high-volume API quotas, you will receive a 100% full refund within 14 calendar days.
- **North America & Global**: Monthly subscription fees are non-refundable for partial periods once accessed, but billing stops immediately upon cancellation for all future cycles.
- **Defective / Interrupted Service**: If a technical failure on our platform prevented you from using your purchased tier, our billing team will issue an expedited prorated refund upon request.
`
  },
  {
    id: 'doc-ai-v3',
    slug: 'ai-disclosure',
    title: 'YamooPay AI Governance & Transparency Statement',
    version: '2.0.0',
    publishedAt: '2026-08-25T00:00:00Z',
    effectiveFrom: '2026-08-25T00:00:00Z',
    legalReviewer: 'AI Governance Lead & Technology Counsel',
    summaryOfChanges: 'EU AI Act Article 50 transparency compliance and prohibited financial practice exclusions.',
    status: 'PUBLISHED',
    contentMarkdown: `
# YamooPay AI Governance Statement (Version 2.0.0)
**Effective Date: August 25, 2026**

## 1. Purpose of Artificial Intelligence at YamooPay
YamooPay uses advanced generative AI models (powered by Google Gemini via \`@google/genai\`) solely to assist users in navigating public financial formulas, interpreting macroeconomic data, and summarizing comparative financial definitions.

## 2. Hard Governance Boundaries & Prohibited Uses
- **No Individual Creditworthiness Scoring**: YamooPay AI is strictly prohibited from evaluating credit scores or performing automated loan underwriting.
- **No Personalized Fiduciary Investment Advice**: The AI Assistant will never instruct you to purchase specific equities, crypto assets, or financial instruments.
- **Transparency Notice**: All conversations in our AI interface are clearly labeled as machine-synthesized dialogues.
`
  },
  {
    id: 'doc-a11y-v3',
    slug: 'accessibility-statement',
    title: 'YamooPay Digital Accessibility Statement',
    version: '2.5.0',
    publishedAt: '2026-08-25T00:00:00Z',
    effectiveFrom: '2026-08-25T00:00:00Z',
    legalReviewer: 'Accessibility Officer',
    summaryOfChanges: 'WCAG 2.2 AA technical baseline adherence and European Accessibility Act (EAA) compliance validation.',
    status: 'PUBLISHED',
    contentMarkdown: `
# YamooPay Accessibility Statement (Version 2.5.0)
**Effective Date: August 25, 2026**

## 1. Conformance Status & Standards
YamooPay is dedicated to ensuring digital accessibility for individuals with disabilities. We continually optimize our user interface in alignment with **WCAG 2.2 Level AA** standards and the European Accessibility Act (EN 301 549).

## 2. Key Accessible Features
- High-contrast visual palette with zero reliance on color alone to convey calculation results.
- Full keyboard operability (Tab, Shift+Tab, Enter, Escape, Arrow keys) across all input masks and modals.
- Screen reader compatibility (NVDA, JAWS, VoiceOver) with explicit ARIA-live regions for dynamic recalculations.

## 3. Feedback & Contact
If you experience any accessibility barrier while using YamooPay, please contact our Accessibility Lead at \`accessibility@yamoopay.com\`. We commit to acknowledging reports within 2 business days.
`
  }
];

export const REGULATORY_CHANGES: RegulatoryChangeRecord[] = [
  {
    id: 'reg-chg-2026-01',
    jurisdiction: 'European Union',
    authority: 'European Commission / AI Office',
    title: 'EU AI Act General Purpose AI & Transparency Rules Enforcement Phase',
    summary: 'Phased implementation of Article 50 transparency obligations requiring explicit user-facing AI badges and downstream developer notices.',
    publicationDate: '2024-07-12',
    effectiveDate: '2025-08-02',
    affectedProducts: ['YamooPay AI Assistant', 'RAG Search Engine'],
    impactLevel: 'HIGH',
    actionRequired: 'Verify persistent AI chat badge and maintain zero-PII prompt telemetry.',
    status: 'COMPLIANT_VERIFIED'
  },
  {
    id: 'reg-chg-2026-02',
    jurisdiction: 'United Kingdom',
    authority: 'Department for Science, Innovation and Technology (DSIT) / ICO',
    title: 'UK Data (Use and Access) Framework Technical Updates',
    summary: 'Refinements to recognized legitimate interests and automated decision-making transparency criteria in the UK.',
    publicationDate: '2025-11-15',
    effectiveDate: '2026-09-30',
    affectedProducts: ['UK User Privacy Pack', 'Telemetry Engine'],
    impactLevel: 'MEDIUM',
    actionRequired: 'Conduct periodic review of UK legitimate interest assessment templates before Q4 2026.',
    status: 'UNDER_LEGAL_REVIEW'
  }
];

export const COMPLIANCE_INCIDENTS: ComplianceIncidentRecord[] = [
  {
    id: 'inc-comp-2026-01',
    incidentType: 'DISCLOSURE_OMISSION',
    severity: 'LOW',
    title: 'Staging Environment Missing Non-Binding Disclaimer on Health Calculator',
    description: 'Automated compliance testing detected a temporary CSS overflow concealing the disclaimer footer on the staging BMI calculator.',
    detectedAt: '2026-08-24T04:15:00Z',
    containmentStatus: 'RESOLVED',
    personalDataBreachAssessed: false,
    regulatorNotificationRequired: false,
    leadInvestigator: 'Consumer Protection Analyst'
  }
];
